import { CronExpressionParser } from "cron-parser";
import { orderBy, where } from "firebase/firestore";
import moment from "moment";
import React, { useEffect, useState } from "react";
import {
    Alert,
    Badge,
    Button,
    ButtonGroup,
    Card,
    OverlayTrigger,
    Tooltip,
} from "react-bootstrap";
import Countdown from "react-countdown";
import { FaBan, FaClock, FaHistory, FaPlus } from "react-icons/fa";
import { Link, useNavigate, useParams } from "react-router";

import {
    RoutineDocument,
    useLocationDocument,
    useRoutineCollection,
    useTaskCollection,
} from "../../api/firestoreWrappers.generated.jsx";
import { routineChangelogRoute } from "../../changelogRoutes.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import { CorvusList } from "../../components/CorvusList";
import Layout, { Title } from "../../components/Layout/Layout";
import ListItemWithToggle from "../../components/ListItemWithToggle";
import { getRoutineDescription } from "../../components/RoutinePreview/index.jsx";
import { useUser } from "../../contexts/user_provider";
import urls from "../../urls.jsx";
import { naturalSort } from "../../utils/sortingUtils.js";

function RoutineItemWithToggle({ routine, tasks }) {
    const { organizationId, locationId } = useParams();
    const [location] = useLocationDocument(organizationId, locationId);
    const timezone = React.useMemo(
        () => location?.timezone || "UTC",
        [location?.timezone],
    );
    const [nextExecutionTime, setNextExecutionTime] = React.useState(0);
    const getDate = React.useCallback(
        (interval) => {
            try {
                return moment(
                    CronExpressionParser.parse(interval, { tz: timezone })
                        .next()
                        .getTime(),
                ).toDate();
            } catch {
                return 0; // Return 0 if parsing fails
            }
        },
        [timezone],
    );

    React.useEffect(() => {
        setNextExecutionTime(getDate(routine.interval));
    }, [getDate, routine.interval]);

    function setRoutineEnabled(enabled) {
        return RoutineDocument(organizationId, locationId, routine.id).update({
            enabled,
        });
    }

    const renderer = ({ days, hours, minutes, seconds, completed }) => {
        return (
            <>
                {completed && "Starting"}
                {days > 0 && `${days}d `}
                {hours > 0 && `${hours}h `}
                {minutes > 0 && `${minutes}m `}
                {seconds > 0 && `${seconds}s `}
            </>
        );
    };

    const { text: routineDescription } = getRoutineDescription({
        routine: routine,
        routineTask: tasks.find((task) => task.id === routine.taskId),
        location,
    });

    return (
        <ListItemWithToggle
            itemKey={routine.id}
            item={routine}
            text={`${routine.name || "Unnamed Routine"} by ${routine.droneId === "ANY" ? "any drone" : routine.droneId}`}
            enabled={routine.enabled}
            onDisable={() => setRoutineEnabled(false)}
            onEnable={() => setRoutineEnabled(true)}
            to={urls.routine(organizationId, locationId, routine.id)}
            title={routineDescription}
            badges={
                routine.enabled === true ? (
                    <Badge pill bg="primary" className="me-1">
                        <FaClock
                            className="me-2"
                            style={{ marginTop: "-2px" }}
                        />
                        {nextExecutionTime !== 0 ? (
                            <Countdown
                                className="d-inline"
                                date={nextExecutionTime}
                                key={nextExecutionTime}
                                renderer={renderer}
                                autoStart={true}
                                intervalDelay={0}
                                onComplete={() => {
                                    // Wait 1 second before updating the next execution time to ensure we set it to the next interval
                                    setTimeout(() => {
                                        setNextExecutionTime(
                                            getDate(routine.interval),
                                        );
                                    }, 1000);
                                }}
                            />
                        ) : (
                            "Invalid"
                        )}
                    </Badge>
                ) : (
                    <Badge pill bg="secondary" className="me-1">
                        <FaBan className="mx-1" />
                    </Badge>
                )
            }
        />
    );
}

export default function RoutineListPage() {
    const navigate = useNavigate();
    const { organizationId, locationId } = useParams();
    const [routines] = useRoutineCollection(organizationId, locationId);
    const [location] = useLocationDocument(organizationId, locationId);
    const [tasks] = useTaskCollection(organizationId, locationId, [
        where("deleted", "==", false),
        orderBy("name", "asc"),
    ]);
    const { getUserIsAdmin } = useUser();
    const [bulkUpdating, setBulkUpdating] = useState(false);

    async function setAllRoutinesEnabled(enabled) {
        setBulkUpdating(true);

        try {
            await Promise.all(
                routines.map((routine) =>
                    RoutineDocument(
                        organizationId,
                        locationId,
                        routine.id,
                    ).update({ enabled }),
                ),
            );
        } finally {
            setBulkUpdating(false);
        }
    }

    useEffect(() => {
        if (!locationId || locationId === "undefined") {
            navigate(urls.locations(organizationId), { replace: true });
        }
    }, [locationId, organizationId, navigate]);

    return (
        <Layout>
            <title>Routines | MyCorvus</title>
            <Breadcrumbs path={breadcrumbPaths.routines} location={location} />
            <Card>
                <Title
                    title={"Routines"}
                    backButtonUrl={urls.location(organizationId, locationId)}
                >
                    {getUserIsAdmin() && (
                        <OverlayTrigger
                            delay={{ show: 500 }}
                            overlay={
                                <Tooltip id="tooltip-changelogs">
                                    Changelogs
                                </Tooltip>
                            }
                        >
                            <Button
                                variant="outline-secondary me-2"
                                as={Link}
                                to={routineChangelogRoute({
                                    organizationId,
                                    locationId,
                                })}
                            >
                                <FaHistory />
                            </Button>
                        </OverlayTrigger>
                    )}
                    <Button
                        variant="outline-success"
                        as={Link}
                        to={urls.routineCreate(organizationId, locationId)}
                    >
                        <FaPlus className="me-2" />
                        Add routine
                    </Button>
                </Title>
                {location && !location.assistant?.enabled && (
                    <Alert variant="danger" className="mx-4 mb-4">
                        {
                            "Assistant is not enabled for this location. Routines won't work."
                        }
                    </Alert>
                )}
                {routines.length === 0 && (
                    <Alert variant="primary" className="mx-4 mb-4">
                        No routines
                    </Alert>
                )}
                {routines.length > 0 && (
                    <>
                        <div className="mx-4 mb-3">
                            <ButtonGroup>
                                <Button
                                    variant="outline-danger"
                                    disabled={
                                        bulkUpdating ||
                                        routines.every(
                                            (routine) =>
                                                routine.enabled === false,
                                        )
                                    }
                                    onClick={() => setAllRoutinesEnabled(false)}
                                >
                                    Disable all
                                </Button>

                                <Button
                                    variant="outline-success"
                                    disabled={
                                        bulkUpdating ||
                                        routines.every(
                                            (routine) =>
                                                routine.enabled === true,
                                        )
                                    }
                                    onClick={() => setAllRoutinesEnabled(true)}
                                >
                                    Enable all
                                </Button>
                            </ButtonGroup>
                        </div>
                        <CorvusList>
                            {routines
                                .sort((a, b) => naturalSort(a.name, b.name))
                                .map((routine) => (
                                    <RoutineItemWithToggle
                                        key={routine.id}
                                        routine={routine}
                                        tasks={tasks}
                                    />
                                ))}
                        </CorvusList>
                    </>
                )}
            </Card>
        </Layout>
    );
}
